import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CaseListTable = ({ 
  cases, 
  onCaseSelect, 
  selectedCaseId, 
  language = 'en',
  direction = 'ltr' 
}) => {
  const labels = {
    caseNumber: {
      en: 'Case Number',
      fr: 'Numéro de dossier',
      ar: 'رقم القضية'
    },
    client: {
      en: 'Client',
      fr: 'Client',
      ar: 'العميل'
    },
    caseType: {
      en: 'Case Type',
      fr: 'Type de dossier',
      ar: 'نوع القضية'
    },
    status: {
      en: 'Status',
      fr: 'Statut',
      ar: 'الحالة'
    },
    nextHearing: {
      en: 'Next Hearing',
      fr: 'Prochaine audience',
      ar: 'الجلسة القادمة'
    },
    actions: {
      en: 'Actions',
      fr: 'Actions',
      ar: 'الإجراءات'
    },
    noCases: {
      en: 'No cases found',
      fr: 'Aucun dossier trouvé',
      ar: 'لم يتم العثور على قضايا'
    },
    view: {
      en: 'View',
      fr: 'Voir',
      ar: 'عرض'
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      intake: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      investigation: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      litigation: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      negotiation: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      appeal: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
      closed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    };
    return colors?.[status] || colors?.intake;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      intake: language === 'ar' ? 'استقبال' : language === 'fr' ? 'Réception' : 'Intake',
      investigation: language === 'ar' ? 'تحقيق' : language === 'fr' ? 'Enquête' : 'Investigation',
      litigation: language === 'ar' ? 'تقاضي' : language === 'fr' ? 'Contentieux' : 'Litigation',
      negotiation: language === 'ar' ? 'تفاوض' : language === 'fr' ? 'Négociation' : 'Negotiation',
      appeal: language === 'ar' ? 'استئناف' : language === 'fr' ? 'Appel' : 'Appeal',
      closed: language === 'ar' ? 'مغلق' : language === 'fr' ? 'Clôturé' : 'Closed'
    };
    return statusLabels?.[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (language === 'ar') {
      return date?.toLocaleDateString('ar-MA');
    }
    return date?.toLocaleDateString('fr-MA');
  };

  if (cases?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 md:p-12 text-center">
        <Icon name="Briefcase" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <p className="text-base md:text-lg text-muted-foreground">{labels?.noCases?.[language]}</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden" style={{ direction }}>
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                {labels?.caseNumber?.[language]}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                {labels?.client?.[language]}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                {labels?.caseType?.[language]}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                {labels?.status?.[language]}
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                {labels?.nextHearing?.[language]}
              </th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">
                {labels?.actions?.[language]}
              </th>
            </tr>
          </thead>
          <tbody>
            {cases?.map((caseItem) => (
              <tr
                key={caseItem?.id}
                className={`border-b border-border hover:bg-muted/50 transition-colors cursor-pointer ${
                  selectedCaseId === caseItem?.id ? 'bg-primary/5' : ''
                }`}
                onClick={() => onCaseSelect(caseItem?.id)}
              >
                <td className="px-4 py-4">
                  <span className="text-sm font-medium text-foreground monospace">
                    {caseItem?.caseNumber}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground">{caseItem?.clientName}</span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-muted-foreground">{caseItem?.caseType}</span>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(caseItem?.status)}`}>
                    {getStatusLabel(caseItem?.status)}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground">{formatDate(caseItem?.nextHearing)}</span>
                </td>
                <td className="px-4 py-4 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onCaseSelect(caseItem?.id);
                    }}
                  >
                    {labels?.view?.[language]}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {cases?.map((caseItem) => (
          <div
            key={caseItem?.id}
            className={`p-4 hover:bg-muted/50 transition-colors cursor-pointer ${
              selectedCaseId === caseItem?.id ? 'bg-primary/5' : ''
            }`}
            onClick={() => onCaseSelect(caseItem?.id)}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-foreground monospace mb-1">
                  {caseItem?.caseNumber}
                </p>
                <p className="text-sm text-foreground">{caseItem?.clientName}</p>
              </div>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(caseItem?.status)}`}>
                {getStatusLabel(caseItem?.status)}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Briefcase" size={16} />
                <span>{caseItem?.caseType}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Calendar" size={16} />
                <span>{formatDate(caseItem?.nextHearing)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CaseListTable;